import React, { useState } from "react";
import { Mail, Phone, Smartphone, Pencil, Trash2, Globe, MapPin, FolderKanban, Plus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { Avatar, Card, HarrodsMark, PageHeader, Segmented } from "../components/pm/bits";
import { OrgBadge } from "../components/pm/MemberSelect";

export function Team() {
  const { team, openDialog, refresh, user } = useApp();
  const [org, setOrg] = useState("all");
  const ep = team.filter((m) => (m.org || "EP Group") === "EP Group");
  const hr = team.filter((m) => m.org === "Harrods");
  const shown = org === "all" ? team : org === "ep" ? ep : hr;
  const del = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the team?`)) return;
    await api.delete(`/team/${m.id}`);
    toast.success("Team member removed");
    refresh();
  };
  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Team Members" subtitle={`${ep.length} EP Group team members · ${hr.length} Harrods contacts`}>
        <div className="flex flex-wrap gap-2">
        <button onClick={() => openDialog("team", { org: "Harrods" })} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#b8975a] bg-[#fbf8f1] px-3 text-sm font-semibold text-[#6e5a32] hover:bg-[#f5efe3]" data-testid="add-harrods-contact-btn">
          <Plus className="h-4 w-4" /> Harrods Contact
        </button>
        <button onClick={() => openDialog("team")} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#00573f] px-3 text-sm font-semibold text-white hover:bg-[#004330]" data-testid="add-team-btn">
          <Plus className="h-4 w-4" /> Add Member
        </button>
        </div>
      </PageHeader>
      <div className="mb-5">
        <Segmented testid="team-org-filter" value={org} onChange={setOrg} options={[{ value: "all", label: `All (${team.length})` }, { value: "ep", label: `EP Group (${ep.length})` }, { value: "harrods", label: `Harrods (${hr.length})` }]} />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((m) => (
          <Card key={m.id} className="group p-5 transition-shadow hover:shadow-md" data-testid={`team-card-${m.id}`}>
            <div className="flex items-center gap-3">
              <Avatar person={m} size={46} className="ring-0" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-bold text-slate-900">{m.name}{m.id === user?.id && <span className="ml-2 rounded bg-[#f5efe3] px-1.5 py-0.5 text-[10px] font-semibold text-[#a08450]">You</span>}</p>
                <p className="truncate text-sm text-slate-500">{m.role}</p>
                <OrgBadge org={m.org} className="mt-1" />
              </div>
              <div className="flex opacity-0 transition-opacity group-hover:opacity-100">
                <button onClick={() => openDialog("team", m)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(m)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
              {m.email && <a href={`mailto:${m.email}`} className="flex items-center gap-2 truncate hover:text-[#00573f]"><Mail className="h-4 w-4 shrink-0 text-slate-400" /> {m.email}</a>}
              {m.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {m.phone}</p>}
              {m.mobile && <p className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-slate-400" /> {m.mobile}</p>}
            </div>
            {!m.email && m.org === "Harrods" && <p className="mt-4 text-xs italic text-slate-400">Placeholder contact - edit to add name &amp; details</p>}
            <div className="mt-4 border-t border-slate-100 pt-3">
              <span className="rounded-full bg-[#e8f1ed] px-2.5 py-1 text-xs font-semibold text-[#00573f]">{m.activeTasks} active task{m.activeTasks === 1 ? "" : "s"}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function Customers() {
  const { customers, openDialog } = useApp();
  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Customers" subtitle={`${customers.length} active account${customers.length === 1 ? "" : "s"}`}>
        <button onClick={() => openDialog("customer")} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#00573f] px-3 text-sm font-semibold text-white hover:bg-[#004330]" data-testid="add-customer-btn">
          <Plus className="h-4 w-4" /> Add Customer
        </button>
      </PageHeader>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {customers.map((c) => (
          <Card key={c.id} className="group overflow-hidden" data-testid={`customer-card-${c.id}`}>
            <div className="flex items-center justify-between bg-[#00573f] px-6 py-5">
              {c.name === "Harrods" ? <HarrodsMark className="text-3xl !text-[#d6b77a]" /> : <p className="text-2xl font-bold text-white">{c.name}</p>}
              <button onClick={() => openDialog("customer", c)} className="rounded-md p-2 text-white/70 hover:bg-white/10 hover:text-white" title="Edit" data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></button>
            </div>
            <div className="space-y-2 p-6 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">{c.contact || "Main contact to be confirmed"}{c.title ? ` · ${c.title}` : ""}</p>
              {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-[#00573f]"><Mail className="h-4 w-4 text-slate-400" /> {c.email}</a>}
              {c.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> {c.phone}</p>}
              {c.web && <a href={`https://${c.web.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#00573f]"><Globe className="h-4 w-4 text-slate-400" /> {c.web}</a>}
              {c.address && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {c.address}</p>}
              {c.notes && <p className="pt-2 text-xs text-slate-400">{c.notes}</p>}
              <div className="pt-3"><span className="inline-flex items-center gap-1.5 rounded-full bg-[#f5efe3] px-2.5 py-1 text-xs font-semibold text-[#6e5a32]"><FolderKanban className="h-3.5 w-3.5" /> {c.projectsCount} projects</span></div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
